import { useQueryClient } from '@tanstack/react-query'
import { Info, X } from 'lucide-react'
import React, { useEffect } from 'react'
import {
  type SubmitHandler,
  useForm,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form'
import { TailSpin } from 'react-loading-icons'
import { toast } from 'sonner'

import NeedsAttentionContext from 'components/context/NeedsAttentionContext'
import { Banner } from 'components/reusable/Banner'
import OpacityTransition from 'components/reusable/OpacityTransition'
import { Button } from 'components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { Switch } from 'components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip'
import { IconButton } from 'slab'
import { usePatchBrokersBrokerAttentionNeededItems } from 'utils/web_api'

import AssigneesFilter from './AssigneesFilter'
import { type SilenceFormInput, SilenceNAVItemsForm } from './NeedsAttentionSilence'
import NoteEditor from './NoteEditor'
import TagFilter from './TagsFilter'
import { setMissingPaymentsQueryDataCallback } from './utils'

export type BulkUpdateItemsDialogProps = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  selectedItems: string[]
}

type BulkUpdateItemsFormInput = {
  broker_ids_to_add: string[]
  broker_ids_to_remove: string[]
  tag_ids_to_add: string[]
  tag_ids_to_remove: string[]
  silence_until_date: string
  content: string
  attempt_to_resolve: boolean
  expected_payment_date_offset: string
}

const HorizontalBreak = () => (
  <>
    <div className='py-1' />
    <hr />
    <div className='py-1' />
  </>
)

const ExpectedPaymentDateOffsetTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='text-sm w-96'>
        Offset date from the beginning of the month of coverage when a payment
        is expected by. This field should only be set on enrollments with
        off-cycle payments that cause NAV noise each month. The value can be
        positive or negative, and is the number of days offset from the
        beginning of the month in which coverage starts that we expect a
        payment. Positive values mean payment is expected after the start of the
        month, negative values mean payment is expected before the start of the
        month.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const AttemptToResolveTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='text-sm w-96'>
        Attempt to resolve after making changes to premium records, coverage or
        enrollment payment mappings. This is not always necessary, but can be
        helpful in cases where the system does not automatically attempt to
        resolve the NAV items.
        If these are manual NAV items, they be forcefully resolved.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const BulkUpdateItemsDialog = ({
  isOpen,
  setIsOpen,
  selectedItems,
}: BulkUpdateItemsDialogProps) => {
  const queryClient = useQueryClient()
  const { getBrokersMissingPaymentsQueryKey } = React.useContext(
    NeedsAttentionContext
  )

  const [assigneesValidationError, setAssigneesValidationError] =
    React.useState<string | null>()
  const [tagsValidationError, setTagsValidationError] = React.useState<
    string | null
  >()

  const { handleSubmit, setValue, watch, reset } =
    useForm<BulkUpdateItemsFormInput>({
      defaultValues: {
        broker_ids_to_add: [],
        broker_ids_to_remove: [],
        tag_ids_to_add: [],
        tag_ids_to_remove: [],
        silence_until_date: '',
        content: '',
        attempt_to_resolve: false,
        expected_payment_date_offset: '',
      },
    })

  const toggleState = (
    key: keyof Omit<
      BulkUpdateItemsFormInput,
      | 'silence_until_date'
      | 'content'
      | 'attempt_to_resolve'
      | 'expected_payment_date_offset'
    >,
    value: string
  ) => {
    const currentValue = watch(key)
    if (currentValue.includes(value)) {
      setValue(
        key,
        currentValue.filter((v) => v !== value)
      )
    } else {
      setValue(key, [...currentValue, value])
    }
  }

  const assigneesToAdd = watch('broker_ids_to_add')
  const assigneesToRemove = watch('broker_ids_to_remove')
  const tagsToAdd = watch('tag_ids_to_add')
  const tagsToRemove = watch('tag_ids_to_remove')
  const noteContent = watch('content')
  const silenceUntilDate = watch('silence_until_date')
  const attemptToResolve = watch('attempt_to_resolve')
  const expectedPaymentDateOffset = watch('expected_payment_date_offset')

  const handleOnOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      reset()
      setAssigneesValidationError(null)
      setTagsValidationError(null)
    }
  }

  useEffect(() => {
    const conflict = assigneesToAdd.find((assignee) =>
      assigneesToRemove.includes(assignee)
    )

    if (conflict) {
      setAssigneesValidationError(
        'Assignees cannot be both added and removed. Please remove duplicate assignees.'
      )
    } else {
      setAssigneesValidationError(null)
    }
  }, [assigneesToAdd, assigneesToRemove])

  useEffect(() => {
    const conflict = tagsToAdd.find((tag) => tagsToRemove.includes(tag))

    if (conflict) {
      setTagsValidationError(
        'Tags cannot be both added and removed. Please remove duplicate tags.'
      )
    } else {
      setTagsValidationError(null)
    }
  }, [tagsToAdd, tagsToRemove])

  const modifyNAVItemsMutation = usePatchBrokersBrokerAttentionNeededItems()

  const onSubmit: SubmitHandler<BulkUpdateItemsFormInput> = data => {
    modifyNAVItemsMutation.mutate(
      {
        data: {
          broker_attention_needed_item_ids: selectedItems,
          ...data,
          expected_payment_date_offset: data.expected_payment_date_offset
            ? Number.parseInt(data.expected_payment_date_offset, 10)
            : undefined,
        },
      },
      {
        onSuccess: (response) => {
          // Update cached data with new NAV items that include updated tags.
          queryClient.setQueryData(
            getBrokersMissingPaymentsQueryKey,
            setMissingPaymentsQueryDataCallback(response)
          )
          toast.success(`${selectedItems.length} items successfully updated!`, {
            position: 'top-center',
          })
          handleOnOpenChange(false)
        },
      }
    )
  }

  const isValidRequest =
    (assigneesToAdd.length > 0 ||
      assigneesToRemove.length > 0 ||
      tagsToAdd.length > 0 ||
      tagsToRemove.length > 0 ||
      silenceUntilDate ||
      attemptToResolve ||
      noteContent ||
      expectedPaymentDateOffset) &&
    !assigneesValidationError &&
    !tagsValidationError

  return (
    <form>
      <Dialog open={isOpen} onOpenChange={handleOnOpenChange}>
        <DialogContent className='flex flex-col max-h-[90vh] p-0'>
          <DialogHeader className='p-6'>
            <DialogTitle className='text-xl'>
              Update Selected NAV Items
            </DialogTitle>
            <DialogDescription>
              {selectedItems.length} items selected
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-2 flex-1 overflow-y-auto p-6'>
            <div className='font-bold text-lg'>Assignees</div>
            <div className='font-semibold text-ds-text-secondary'>
              Add assignees:
            </div>
            <AssigneesFilter
              selectedValues={assigneesToAdd}
              onSelectValue={(value) => toggleState('broker_ids_to_add', value)}
            />
            <div className='font-semibold text-ds-text-secondary'>
              Remove assignees:
            </div>
            <AssigneesFilter
              selectedValues={assigneesToRemove}
              onSelectValue={(value) =>
                toggleState('broker_ids_to_remove', value)}
            />
            {assigneesValidationError && (
              <OpacityTransition>
                <Banner
                  type='critical'
                  titleText='Validation error'
                  className='mt-2'
                >
                  {assigneesValidationError}
                </Banner>
              </OpacityTransition>
            )}

            <HorizontalBreak />

            <div className='font-bold text-lg'>Tags</div>
            <div className='font-semibold text-ds-text-secondary'>
              Add tags:
            </div>
            <TagFilter
              selectedValues={tagsToAdd}
              onSelectValue={(value) => toggleState('tag_ids_to_add', value)}
            />
            <div className='font-semibold text-ds-text-secondary'>
              Remove tags:
            </div>
            <TagFilter
              selectedValues={tagsToRemove}
              onSelectValue={(value) => toggleState('tag_ids_to_remove', value)}
            />
            {tagsValidationError && (
              <OpacityTransition>
                <Banner
                  type='critical'
                  titleText='Validation error'
                  className='mt-2'
                >
                  {tagsValidationError}
                </Banner>
              </OpacityTransition>
            )}

            <HorizontalBreak />

            <div className='font-bold text-lg'>Add a Note</div>
            <NoteEditor setValue={(value) => setValue('content', value)} />

            <HorizontalBreak />
            <div className='font-bold text-lg'>Silence</div>
            {/* <div className='font-semibold text-ds-text-secondary'>Add tags:</div> */}
            <SilenceNAVItemsForm
              setValue={
                setValue as unknown as UseFormSetValue<SilenceFormInput>
              }
              watch={watch as unknown as UseFormWatch<SilenceFormInput>}
              isPending={modifyNAVItemsMutation.isPending}
              displayNoteEditor={false}
            />

            <HorizontalBreak />

            <div className='flex items-center space-x-2'>
              <div className='font-bold text-lg'>
                Expected Payment Date Offset
              </div>
              <ExpectedPaymentDateOffsetTooltip />
            </div>
            <div className='flex items-center space-x-2'>
              <Select
                onValueChange={(value) =>
                  setValue('expected_payment_date_offset', value)}
                value={expectedPaymentDateOffset}
              >
                <SelectTrigger className='w-[100px]'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 61 }, (_, i) => i - 30).map(
                    (value, index) => (
                      <SelectItem key={index} value={value.toString()}>
                        {value.toString()}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
              <IconButton
                onClick={() =>
                  setValue('expected_payment_date_offset', '')}
                variant='destructive'
                label='Remove filter'
                icon={X}
              />
            </div>

            <HorizontalBreak />

            <div className='flex items-center space-x-2'>
              <div className='font-bold text-lg'>Attempt to Resolve</div>
              <AttemptToResolveTooltip />
            </div>
            <Switch
              checked={attemptToResolve}
              onCheckedChange={(value) => setValue('attempt_to_resolve', value)}
            />
          </div>

          <DialogFooter className='flex items-center justify-end mt-4 p-6'>
            <Button
              type='reset'
              onClick={() => handleOnOpenChange(false)}
              variant='secondary'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={!isValidRequest || modifyNAVItemsMutation.isPending}
              onClick={() => handleSubmit(onSubmit)()}
            >
              {modifyNAVItemsMutation.isPending
                ? (
                  <div className='flex items-center h-[100%]'>
                    <span>Updating...</span>
                    <TailSpin fill='#FFF' stroke='#FFF' className='h-[90%]' />
                  </div>
                  )
                : (
                    'Update'
                  )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  )
}

export default BulkUpdateItemsDialog
